import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, MessageCircle, FileText, Building, 
  LogOut, Settings, Eye, Edit, Trash2, Plus,
  CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SMTPSettings from '../components/SMTPSettings';

const AdminDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [content, setContent] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('admin_token');

  const apiHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load dashboard stats
      const statsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/stats`,
        { headers: apiHeaders }
      );
      setStats(statsResponse.data);

      // Load inquiries
      const inquiriesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries?limit=10`,
        { headers: apiHeaders }
      );
      setInquiries(inquiriesResponse.data);

      // Load content items
      const contentResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content`,
        { headers: apiHeaders }
      );
      setContent(contentResponse.data);

      // Load companies
      const companiesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/companies`,
        { headers: apiHeaders }
      );
      setCompanies(companiesResponse.data);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      if (error.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${inquiryId}`,
        { status: newStatus },
        { headers: apiHeaders }
      );
      
      // Reload inquiries
      loadDashboardData();
    } catch (error) {
      console.error('Error updating inquiry:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      closed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
    };
    
    const config = statusConfig[status] || statusConfig.new;
    const IconComponent = config.icon;
    
    return (
      <Badge className={config.color}>
        <IconComponent className="h-3 w-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Building className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">Teng Food Holdings CMS</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onLogout}
                className="text-red-600 hover:text-red-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <MessageCircle className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Inquiries</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.inquiries.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">New Inquiries</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.inquiries.new}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <FileText className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Content Items</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.content_items}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Building className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Companies</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.companies}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="inquiries" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="inquiries">Contact Inquiries</TabsTrigger>
            <TabsTrigger value="content">Content Management</TabsTrigger>
            <TabsTrigger value="companies">Company Info</TabsTrigger>
            <TabsTrigger value="smtp">SMTP Settings</TabsTrigger>
          </TabsList>

          {/* Contact Inquiries Tab */}
          <TabsContent value="inquiries">
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Contact Inquiries</h2>
                  <Button size="sm" onClick={loadDashboardData}>
                    Refresh
                  </Button>
                </div>

                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div key={inquiry.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 mb-2">{inquiry.subject}</h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <div className="flex items-start">
                              <span className="font-semibold text-gray-700 min-w-[80px]">Name:</span>
                              <span className="text-gray-600">{inquiry.name}</span>
                            </div>
                            
                            <div className="flex items-start">
                              <span className="font-semibold text-gray-700 min-w-[80px]">Email:</span>
                              <span className="text-gray-600">{inquiry.email}</span>
                            </div>
                            
                            {inquiry.phone && (
                              <div className="flex items-start">
                                <span className="font-semibold text-gray-700 min-w-[80px]">Phone:</span>
                                <span className="text-gray-600">{inquiry.phone}</span>
                              </div>
                            )}
                            
                            {inquiry.company && (
                              <div className="flex items-start">
                                <span className="font-semibold text-gray-700 min-w-[80px]">Company:</span>
                                <span className="text-gray-600">{inquiry.company}</span>
                              </div>
                            )}
                            
                            <div className="flex items-start">
                              <span className="font-semibold text-gray-700 min-w-[80px]">Type:</span>
                              <span className="text-gray-600 capitalize">
                                {inquiry.inquiry_type?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                        {getStatusBadge(inquiry.status)}
                      </div>
                      
                      <div className="border-t pt-3 mt-3">
                        <p className="font-semibold text-gray-700 text-sm mb-1">Message:</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{inquiry.message}</p>
                      </div>
                      
                      <div className="flex justify-between items-center mt-4 pt-3 border-t">
                        <span className="text-xs text-gray-500">
                          Submitted: {new Date(inquiry.created_at).toLocaleString()}
                        </span>
                        
                        <div className="space-x-2">
                          {inquiry.status === 'new' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateInquiryStatus(inquiry.id, 'in_progress')}
                            >
                              Mark In Progress
                            </Button>
                          )}
                          {inquiry.status === 'in_progress' && (
                            <Button 
                              size="sm" 
                              onClick={() => updateInquiryStatus(inquiry.id, 'resolved')}
                            >
                              Mark Resolved
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {inquiries.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No inquiries found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Management Tab */}
          <TabsContent value="content">
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Content Items</h2>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Content
                  </Button>
                </div>

                <div className="space-y-4">
                  {content.map((item) => (
                    <div key={item.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900">{item.title}</h3>
                          <p className="text-sm text-gray-600">
                            Section: {item.section} • Type: {item.content_type}
                          </p>
                          <p className="text-sm text-gray-600">Key: {item.key}</p>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {content.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No content items found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Information Tab */}
          <TabsContent value="companies">
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Company Information</h2>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Company
                  </Button>
                </div>

                <div className="space-y-4">
                  {companies.map((company) => (
                    <div key={company.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900">{company.company_name}</h3>
                          <p className="text-sm text-gray-600">UEN: {company.uen}</p>
                          <p className="text-sm text-gray-600">Category: {company.category}</p>
                          <p className="text-sm text-gray-600">Email: {company.email}</p>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {companies.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No companies found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* SMTP Settings Tab */}
          <TabsContent value="smtp">
            <SMTPSettings />
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;